import { useState } from "react"
import useFetch from "../../hooks/useFetch"
import useShipmentSnackbar from "./useShipmentSnackbar"

export default () => {

  const fetch = useFetch()
  const snackbar = useShipmentSnackbar()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState()

  /**
   * Calls fetch() and supresses all errors with a snackbar message and console output.
   * 
   * @param nosupress - By default, errors are supressed. If nosupress is true, errors are thrown instead.
   * @param noparse - By default the result is expected to be JSON. If noparse is true, the response will not be converted and the full response will be returned.
   * @returns shipmentFetch, isLoading, error. shipmentFetch returns the result of a fetch() call, converted into JSON. isLoading is true while the fetch is running. error contains the most recent error.
   * 
   * @example
   * const { shipmentFetch } = useShipmentFetch()
   * const json = shipmentFetch('/get')
   * 
   * @example
   * const { shipmentFetch, isLoading, error } = useShipmentFetch()
   * const json = shipmentFetch('/get')
   * return <>
   *  <div>{isLoading ? 'loading...' : JSON.stringify(json)}</div>
   *  {error && <div>Error: ${error}</div>}
   * </>
   * 
   * @see https://stackoverflow.com/questions/51297206/fetch-api-global-error-handler
   */
  const shipmentFetch = async (url, ...params) => {
    try {
      setIsLoading(true)
      const response = await fetch(url, ...params)

      if (!response.ok) throw response

      if (params.noparse) return response

      const json = response.json()
      if (json.error) throw json.error

      return json

    } catch (error) {
      setError(error)

      if (params.nosupress) throw error

      console.error('Fetch error', error)
      snackbar.newMessage({ message: 'An error has occurred', severity: 'failure' })

    } finally {
      setIsLoading(false)

    }
  }
  
  return {
    shipmentFetch,
    isLoading,
    error
  }
}
